import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { createPortal, flushSync } from "react-dom";
import { InputText } from "primereact/inputtext";
import { usePopoutDocuments } from "../../utils/popoutWindowsRegistry";
import { useChartsStore } from "../../store/chartStore";
import useWorkspaceStore from "../../store/workspaceStore";
import useWatchlistStore from "../../store/watchlistStore";
import useBookByPriceStore from "../../store/bookByPriceStore";
import marketdata from "../../data/Services/marketdata";
import timeOptions from "./timeOptions";
import { getChartTypes } from "./chartTypes";

export const PANEL_CAPABILITIES = {
  chart: [
    "symbol",
    "periodicity",
    "charttype",
    "indicator",
    "barcolor",
    "debug",
  ],
  grid: ["symbol"],
  bookByPrice: ["symbol"],
  positions: [],
  openOrders: [],
  log: [],
};

const SymbolSpotlight = forwardRef((_props, ref) => {
  const [isVisible, setIsVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  // PoC #popout: document of the popout window where typing started — the
  // overlay portals there so the spotlight opens under the user's eyes.
  const [sourceDoc, setSourceDoc] = useState(null);
  const popoutDocs = usePopoutDocuments();
  const inputRef = useRef(null);
  const mouseMovedRef = useRef(false); // Track if mouse has moved

  const getSelectedChart = useChartsStore((state) => state.getSelectedChart);
  const updateChartConfig = useChartsStore((state) => state.updateChartConfig);
  const changeChartSymbol = useChartsStore((state) => state.changeChartSymbol);
  const fetchChartData = useChartsStore((state) => state.fetchChartData);
  const focusedPanelType = useWorkspaceStore((state) => state.focusedPanelType);
  const addSymbolToWatchlist = useWatchlistStore((state) => state.addSymbol);
  const setBookByPriceSymbol = useBookByPriceStore((state) => state.setSymbol);

  // Load symbols and periodicities, filter when search text changes
  useEffect(() => {
    // Reset mouse moved flag when list changes
    mouseMovedRef.current = false;

    if (searchText.length >= 1) {
      const items = [];
      const capabilities =
        PANEL_CAPABILITIES[focusedPanelType] || PANEL_CAPABILITIES.chart;

      // Special case: Only show DEBUG commands when search starts with "DEBUG:"
      if (
        capabilities.includes("debug") &&
        searchText.toUpperCase().startsWith("DEBUG:")
      ) {
        // Check for DEBUG:RENDER command
        if ("DEBUG:RENDER".startsWith(searchText.toUpperCase())) {
          items.push({
            type: "debug",
            value: "DEBUG:RENDER",
            label: "DEBUG:RENDER",
            I: "DEBUG:RENDER",
            D: "Open Debug Panel",
          });
        }

        // Check for DEBUG:SERIES command
        if ("DEBUG:SERIES".startsWith(searchText.toUpperCase())) {
          items.push({
            type: "debug-series",
            value: "DEBUG:SERIES",
            label: "DEBUG:SERIES",
            I: "DEBUG:SERIES",
            D: "Show All Series Data",
          });
        }

        // Check for DEBUG:INDICATOR command
        if ("DEBUG:INDICATOR".startsWith(searchText.toUpperCase())) {
          items.push({
            type: "debug-indicator",
            value: "DEBUG:INDICATOR",
            label: "DEBUG:INDICATOR",
            I: "DEBUG:INDICATOR",
            D: "Batch Test All Indicators",
          });
        }

        // Check for DEBUG:FACTORY command
        if ("DEBUG:FACTORY".startsWith(searchText.toUpperCase())) {
          items.push({
            type: "debug-factory",
            value: "DEBUG:FACTORY",
            label: "DEBUG:FACTORY",
            I: "DEBUG:FACTORY",
            D: "🏭 Factory Reset (Clear All Storage)",
          });
        }
      }

      // Check periodicities (only if panel supports it)
      if (capabilities.includes("periodicity")) {
        // Add keyboard shortcuts: D → 1D, S → 1W, M → 1M
        const periodicityShortcuts = {
          D: "1D",
          S: "1W",
          M: "1M",
        };

        const filteredPeriodicities = timeOptions
          .filter((option) => {
            const search = searchText.toLowerCase();
            const matchesValue = option.value.toLowerCase().includes(search);
            const matchesLabel = option.label.toLowerCase().includes(search);
            // Check if search is a shortcut key
            const isShortcut =
              periodicityShortcuts[searchText.toUpperCase()] === option.value;
            return matchesValue || matchesLabel || isShortcut;
          })
          .map((option) => ({
            type: "periodicity",
            value: option.value,
            label: option.label,
            I: option.value,
            D: option.label,
          }));
        items.push(...filteredPeriodicities);
      } // end periodicity capability check

      // Then check chart types
      if (capabilities.includes("charttype")) {
        const filteredChartTypes = getChartTypes()
          .filter(
            (option) =>
              option.value.toLowerCase().includes(searchText.toLowerCase()) ||
              option.label.toLowerCase().includes(searchText.toLowerCase()),
          )
          .map((option) => ({
            type: "charttype",
            value: option.value,
            label: option.label,
            I: option.label, // Use label as display code for chart types
            D: `Tipo de gráfico: ${option.label}`,
          }));
        items.push(...filteredChartTypes);
      } // end charttype capability check

      // Finally check symbols (start filtering from first character)
      const loadedSymbols = marketdata.symbols;
      const symbols = Array.from(loadedSymbols, ([key, value]) => ({
        type: "symbol",
        label: key,
        value: key,
        ...value,
      }));

      const filteredSymbols = symbols.filter((symbol) =>
        symbol.I.toLowerCase().startsWith(searchText.toLowerCase()),
      );
      items.push(...filteredSymbols);

      setFilteredItems(items.slice(0, 100)); // Limit to 100 items for performance
      setSelectedIndex(0);
    } else {
      setFilteredItems([]);
      setSelectedIndex(0);
    }
  }, [searchText, focusedPanelType]);

  // Global keyboard handler — attached to the main document AND every popout
  // window document (keydown lands on the document of the window that has
  // focus; the main listener never sees keys typed over a popout).
  useEffect(() => {
    const makeHandler = (doc) => (e) => {
      // Don't trigger if user is typing in an input/textarea or spotlight is already open
      if (
        isVisible ||
        e.target.tagName === "INPUT" ||
        e.target.tagName === "TEXTAREA" ||
        e.ctrlKey ||
        e.metaKey ||
        e.altKey
      ) {
        return;
      }

      // Don't trigger if any dropdown is open (check for dropdown-content visibility)
      // Overlays may live in the main document or in the typing window's document.
      const dropdownOpen =
        document.querySelector(".dropdown-content") ||
        (doc !== document && doc.querySelector(".dropdown-content"));
      if (dropdownOpen) {
        return;
      }

      // Don't trigger if any modal/dialog is open
      // PrimeReact modals create a .p-dialog-mask overlay when open
      const modalOpen =
        document.querySelector(".p-dialog-mask, .p-overlay-mask") ||
        (doc !== document &&
          doc.querySelector(".p-dialog-mask, .p-overlay-mask"));
      if (modalOpen) {
        return;
      }

      // Don't open spotlight if focused panel has no capabilities
      const caps =
        PANEL_CAPABILITIES[useWorkspaceStore.getState().focusedPanelType];
      if (caps && caps.length === 0) {
        return;
      }

      // Only handle alphanumeric characters
      if (e.key.length === 1 && /[a-zA-Z0-9]/.test(e.key)) {
        e.preventDefault();
        // flushSync: commit the open NOW so the input exists inside this
        // trusted keydown stack — iOS only honors programmatic focus() there
        // (the focus-on-visible effect runs too late for Safari and also
        // skips touch devices). Opened BY TYPING ⇒ a hardware keyboard is
        // attached ⇒ focusing is safe on touch devices too (no on-screen IME
        // pops while a hardware keyboard is connected). Without this, an
        // iPhone + external keyboard got the first letter only and lost
        // focus.
        flushSync(() => {
          setSearchText(e.key.toUpperCase());
          setSourceDoc(doc === document ? null : doc);
          setIsVisible(true);
        });
        inputRef.current?.focus();
      }
    };

    const attached = [document, ...popoutDocs].map((doc) => {
      const handler = makeHandler(doc);
      doc.addEventListener("keydown", handler);
      return [doc, handler];
    });
    return () =>
      attached.forEach(([doc, handler]) =>
        doc.removeEventListener("keydown", handler),
      );
  }, [isVisible, popoutDocs]);

  // Focus input when spotlight becomes visible (skip on touch devices to avoid IME toolbar)
  useEffect(() => {
    if (isVisible && inputRef.current) {
      // Check if device has coarse pointer (touch) - skip auto-focus to avoid Android IME toolbar
      const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;
      if (!isTouchDevice) {
        inputRef.current.focus();
      }
    }
  }, [isVisible]);

  // Track mouse movement to prevent hover from interfering with keyboard navigation
  useEffect(() => {
    const handleMouseMove = () => {
      mouseMovedRef.current = true;
    };

    if (isVisible) {
      const doc = sourceDoc || document;
      doc.addEventListener("mousemove", handleMouseMove);
      return () => doc.removeEventListener("mousemove", handleMouseMove);
    }
  }, [isVisible, sourceDoc]);

  // Handle keyboard navigation within spotlight
  const handleSpotlightKeydown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeSpotlight();
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filteredItems.length > 0) {
        selectItem(filteredItems[selectedIndex]);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev < filteredItems.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev > 0 ? prev - 1 : filteredItems.length - 1,
      );
    }
  };

  const selectItem = (item) => {
    // Special case: DEBUG:RENDER command to open debug panel
    if (
      item.type === "debug" ||
      (item.type === "symbol" && item.value === "DEBUG:RENDER")
    ) {
      // Dispatch custom event to open debug panel
      window.dispatchEvent(new CustomEvent("openDebugPanel"));
      closeSpotlight();
      return;
    }

    // Special case: DEBUG:SERIES command to open series debug panel
    if (
      item.type === "debug-series" ||
      (item.type === "symbol" && item.value === "DEBUG:SERIES")
    ) {
      // Dispatch custom event to open series debug panel
      window.dispatchEvent(new CustomEvent("openDebugSeriesPanel"));
      closeSpotlight();
      return;
    }

    // Special case: DEBUG:INDICATOR command to open indicator batch tester
    if (
      item.type === "debug-indicator" ||
      (item.type === "symbol" && item.value === "DEBUG:INDICATOR")
    ) {
      // Dispatch custom event to open indicator batch tester
      window.dispatchEvent(new CustomEvent("openIndicatorBatchTester"));
      closeSpotlight();
      return;
    }

    // Special case: DEBUG:FACTORY command to perform factory reset
    if (
      item.type === "debug-factory" ||
      (item.type === "symbol" && item.value === "DEBUG:FACTORY")
    ) {
      // Trigger factory reset via devTools
      if (window.devTools && window.devTools.factoryReset) {
        window.devTools.factoryReset();
      } else {
        console.error("devTools.factoryReset not available");
      }
      closeSpotlight();
      return;
    }

    if (item.type === "symbol" && focusedPanelType === "grid") {
      addSymbolToWatchlist(item.value);
      closeSpotlight();
      return;
    }

    if (item.type === "symbol" && focusedPanelType === "bookByPrice") {
      setBookByPriceSymbol(item.value);
      closeSpotlight();
      return;
    }

    const selectedChart = getSelectedChart();
    if (!selectedChart) {
      closeSpotlight();
      return;
    }

    const selectedChartId = selectedChart.id;

    if (item.type === "symbol") {
      changeChartSymbol(selectedChartId, item.value);
    } else if (item.type === "periodicity") {
      updateChartConfig(selectedChartId, {
        periodicity: item.value,
        xExtents: null,
      });
      fetchChartData(selectedChartId);
    } else if (item.type === "charttype") {
      updateChartConfig(selectedChartId, { chartType: item.value });
    }

    closeSpotlight();
  };

  const closeSpotlight = () => {
    setIsVisible(false);
    setSearchText("");
    setFilteredItems([]);
    setSelectedIndex(0);
    setSourceDoc(null);
    mouseMovedRef.current = false;
  };

  const openSpotlight = () => {
    setIsVisible(true);
    setSearchText("");
    mouseMovedRef.current = false;
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    open: openSpotlight,
    close: closeSpotlight,
  }));

  if (!isVisible) return null;

  const content = (
    <>
      <style>{`
                .spotlight-scroll-area::-webkit-scrollbar {
                    width: 6px;
                }
                .spotlight-scroll-area::-webkit-scrollbar-track {
                    background: transparent;
                }
                .spotlight-scroll-area::-webkit-scrollbar-thumb {
                    background: var(--surface-border);
                    border-radius: 3px;
                }
                .spotlight-scroll-area::-webkit-scrollbar-thumb:hover {
                    background: var(--text-color-secondary);
                }
            `}</style>
      <div
        className="symbol-spotlight-overlay"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(2px)",
          display: "flex",
          // Anchored to the upper third (not centered): on phones the
          // on-screen keyboard covers the lower half and a centered panel
          // loses its input — top-anchoring keeps input + first results
          // visible on any window size (Spotlight convention on desktop too).
          alignItems: "flex-start",
          justifyContent: "center",
          // The margins live HERE (the container just fills 100%): ≥16px on
          // every side, growing to the safe-area inset near a notch/home bar.
          boxSizing: "border-box",
          paddingTop: "clamp(16px, 12vh, 120px)",
          paddingLeft: "max(16px, env(safe-area-inset-left, 0px))",
          paddingRight: "max(16px, env(safe-area-inset-right, 0px))",
          paddingBottom: "max(16px, env(safe-area-inset-bottom, 0px))",
          zIndex: 9999,
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            closeSpotlight();
          }
        }}
        data-evo-id="navbar-symbol-spotlight-overlay"
      >
        <div
          className="symbol-spotlight-container"
          style={{
            background: "var(--surface-ground)",
            border: "1px solid var(--surface-border)",
            borderRadius: "12px",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
            // Fixed size (prevents jumping) clamped to the overlay's padded
            // area: 100% here = current window viewport minus the overlay
            // margins/safe-areas above — fits ANY window (phone, popout)
            // without knowing vw/vh.
            width: "min(500px, 100%)",
            height: "400px",
            maxHeight: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Search Input */}
          <div style={{ padding: "20px 20px 10px 20px", flexShrink: 0 }}>
            <InputText
              ref={inputRef}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value.toUpperCase())}
              onKeyDown={handleSpotlightKeydown}
              placeholder={
                focusedPanelType === "grid"
                  ? "Adicionar símbolo à watchlist..."
                  : "Digite o símbolo do ativo..."
              }
              style={{
                width: "100%",
                fontSize: "18px",
                padding: "12px 16px",
                border: "none",
                outline: "none",
                background: "transparent",
                color: "var(--text-color)",
              }}
              data-evo-id="navbar-symbol-spotlight-input"
            />
          </div>
          {/* Results/Content Area - takes remaining space */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            {/* Results List */}
            {filteredItems.length > 0 && (
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  borderTop: "1px solid var(--surface-border)",
                  maxHeight: "300px", // Explicit max height to force scrolling
                  scrollbarWidth: "thin", // For Firefox
                  scrollbarColor: "var(--surface-border) transparent", // For Firefox
                }}
                className="spotlight-scroll-area"
              >
                {(() => {
                  const debugItems = filteredItems.filter(
                    (item) =>
                      item.type === "debug" ||
                      item.type === "debug-series" ||
                      item.type === "debug-indicator" ||
                      item.type === "debug-factory",
                  );
                  const periodicities = filteredItems.filter(
                    (item) => item.type === "periodicity",
                  );
                  const chartTypes = filteredItems.filter(
                    (item) => item.type === "charttype",
                  );
                  const symbols = filteredItems.filter(
                    (item) => item.type === "symbol",
                  );
                  let currentIndex = 0;

                  return (
                    <>
                      {/* Render DEBUG command */}
                      {debugItems.map((item) => {
                        const globalIndex = currentIndex++;
                        return (
                          <div
                            key={`${item.type}-${item.value}`}
                            onClick={() => selectItem(item)}
                            style={{
                              padding: "8px 20px",
                              height: "40px",
                              cursor: "pointer",
                              background:
                                globalIndex === selectedIndex
                                  ? "var(--surface-hover)"
                                  : "transparent",
                              color: "var(--text-color)",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              borderLeft:
                                globalIndex === selectedIndex
                                  ? "3px solid var(--primary-color)"
                                  : "3px solid transparent",
                            }}
                            onMouseEnter={() => {
                              if (mouseMovedRef.current) {
                                setSelectedIndex(globalIndex);
                              }
                            }}
                            data-evo-id={`navbar-symbol-spotlight-debug-item-${item.value}`}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                flex: 1,
                                height: "100%",
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: "bold",
                                  fontSize: "16px",
                                  minWidth: "80px",
                                  textAlign: "left",
                                }}
                              >
                                {item.I}
                              </div>
                              <div
                                style={{
                                  fontSize: "14px",
                                  opacity: 0.7,
                                  flex: 1,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  textAlign: "left",
                                }}
                              >
                                {item.D}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  opacity: 0.6,
                                  backgroundColor: "rgba(255, 100, 0, 0.2)",
                                  color: "#ff6400",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                  minWidth: "60px",
                                  textAlign: "center",
                                }}
                              >
                                🔧 Debug
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {/* Separator after DEBUG */}
                      {debugItems.length > 0 &&
                        (periodicities.length > 0 ||
                          chartTypes.length > 0 ||
                          symbols.length > 0) && (
                          <div
                            style={{
                              height: "1px",
                              background: "var(--surface-border)",
                              margin: "4px 20px",
                              opacity: 0.3,
                            }}
                          />
                        )}

                      {/* Render Periodicities */}
                      {periodicities.map((item) => {
                        const globalIndex = currentIndex++;
                        return (
                          <div
                            key={`${item.type}-${item.value}`}
                            onClick={() => selectItem(item)}
                            style={{
                              padding: "8px 20px",
                              height: "40px",
                              cursor: "pointer",
                              background:
                                globalIndex === selectedIndex
                                  ? "var(--surface-hover)"
                                  : "transparent",
                              color: "var(--text-color)",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              borderLeft:
                                globalIndex === selectedIndex
                                  ? "3px solid var(--primary-color)"
                                  : "3px solid transparent",
                            }}
                            onMouseEnter={() => {
                              if (mouseMovedRef.current) {
                                setSelectedIndex(globalIndex);
                              }
                            }}
                            data-evo-id={`navbar-symbol-spotlight-periodicity-item-${item.value}`}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                flex: 1,
                                height: "100%",
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: "bold",
                                  fontSize: "16px",
                                  minWidth: "80px",
                                  textAlign: "left",
                                }}
                              >
                                {item.I}
                              </div>
                              <div
                                style={{
                                  fontSize: "14px",
                                  opacity: 0.7,
                                  flex: 1,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  textAlign: "left",
                                }}
                              >
                                {item.D}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  opacity: 0.6,
                                  backgroundColor: "var(--surface-border)",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                  minWidth: "60px",
                                  textAlign: "center",
                                }}
                              >
                                Período
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {/* Separator between periodicities and chart types */}
                      {periodicities.length > 0 && chartTypes.length > 0 && (
                        <div
                          style={{
                            height: "1px",
                            background: "var(--surface-border)",
                            margin: "4px 20px",
                            opacity: 0.3,
                          }}
                        />
                      )}

                      {/* Render Chart Types */}
                      {chartTypes.map((item) => {
                        const globalIndex = currentIndex++;
                        return (
                          <div
                            key={`${item.type}-${item.value}`}
                            onClick={() => selectItem(item)}
                            style={{
                              padding: "8px 20px",
                              height: "40px",
                              cursor: "pointer",
                              background:
                                globalIndex === selectedIndex
                                  ? "var(--surface-hover)"
                                  : "transparent",
                              color: "var(--text-color)",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              borderLeft:
                                globalIndex === selectedIndex
                                  ? "3px solid var(--primary-color)"
                                  : "3px solid transparent",
                            }}
                            onMouseEnter={() => {
                              if (mouseMovedRef.current) {
                                setSelectedIndex(globalIndex);
                              }
                            }}
                            data-evo-id={`navbar-symbol-spotlight-charttype-item-${item.value}`}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                flex: 1,
                                height: "100%",
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: "bold",
                                  fontSize: "16px",
                                  minWidth: "80px",
                                  textAlign: "left",
                                }}
                              >
                                {item.I}
                              </div>
                              <div
                                style={{
                                  fontSize: "14px",
                                  opacity: 0.7,
                                  flex: 1,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  textAlign: "left",
                                }}
                              >
                                {item.D}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  opacity: 0.6,
                                  backgroundColor: "var(--surface-border)",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                  minWidth: "60px",
                                  textAlign: "center",
                                }}
                              >
                                Gráfico
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {/* Separator between chart types and symbols */}
                      {(periodicities.length > 0 || chartTypes.length > 0) &&
                        symbols.length > 0 && (
                          <div
                            style={{
                              height: "1px",
                              background: "var(--surface-border)",
                              margin: "4px 20px",
                              opacity: 0.3,
                            }}
                          />
                        )}

                      {/* Render Symbols */}
                      {symbols.map((item) => {
                        const globalIndex = currentIndex++;
                        return (
                          <div
                            key={`${item.type}-${item.value}`}
                            onClick={() => selectItem(item)}
                            style={{
                              padding: "8px 20px",
                              height: "40px",
                              cursor: "pointer",
                              background:
                                globalIndex === selectedIndex
                                  ? "var(--surface-hover)"
                                  : "transparent",
                              color: "var(--text-color)",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              borderLeft:
                                globalIndex === selectedIndex
                                  ? "3px solid var(--primary-color)"
                                  : "3px solid transparent",
                            }}
                            onMouseEnter={() => {
                              if (mouseMovedRef.current) {
                                setSelectedIndex(globalIndex);
                              }
                            }}
                            data-evo-id={`navbar-symbol-spotlight-symbol-item-${item.value}`}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                flex: 1,
                                height: "100%",
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: "bold",
                                  fontSize: "16px",
                                  minWidth: "80px",
                                  textAlign: "left",
                                }}
                              >
                                {item.I}
                              </div>
                              <div
                                style={{
                                  fontSize: "14px",
                                  opacity: 0.7,
                                  flex: 1,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  textAlign: "left",
                                }}
                              >
                                {item.D}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  opacity: 0.6,
                                  backgroundColor: "var(--surface-border)",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                  minWidth: "60px",
                                  textAlign: "center",
                                }}
                              >
                                Símbolo
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            )}

            {/* No results message */}
            {searchText.length >= 1 && filteredItems.length === 0 && (
              <div
                style={{
                  padding: "20px",
                  textAlign: "center",
                  color: "var(--text-color-secondary)",
                  borderTop: "1px solid var(--surface-border)",
                }}
              >
                {focusedPanelType === "grid"
                  ? "Nenhum símbolo encontrado"
                  : "Nenhum período, gráfico ou símbolo encontrado"}
              </div>
            )}

            {/* Instructions */}
            {searchText.length < 1 && (
              <div
                style={{
                  padding: "20px",
                  textAlign: "center",
                  color: "var(--text-color-secondary)",
                  fontSize: "14px",
                }}
              >
                {focusedPanelType === "grid"
                  ? "Digite para adicionar símbolo à watchlist"
                  : "Digite para buscar períodos, tipos de gráfico e símbolos"}
              </div>
            )}
          </div>{" "}
          {/* Close Results/Content Area */}
        </div>
      </div>
    </>
  );

  // PoC #popout: open the overlay in the window where the user typed.
  // ALWAYS portaled (fallback: main document.body) — rendered inline it
  // inherits display:none from the SymbolDropdown wrapper, which the navbar
  // hides in extended-chart-menu mode ("Trocar símbolo…" opened an invisible
  // spotlight).
  return createPortal(content, (sourceDoc ?? document).body);
});

SymbolSpotlight.displayName = "SymbolSpotlight";

export default SymbolSpotlight;
